/**
 * Staff Renderer
 * Uses VexFlow to render musical notation on staff
 */

import { MusicTheory } from '../utils/music-theory';

// Window.Vex augmentation; the global `Vex` itself is declared in src/vexflow.d.ts
declare global {
    interface Window {
        Vex: VexFlowAPI;
    }
}

// Type definitions
export type ClefType = 'treble' | 'bass';

interface RenderOptions {
    highlight?: boolean;
}

export class StaffRenderer {
    private container: HTMLElement | null;
    private renderer: VexFlowAPI = null;
    private context: VexFlowAPI = null;
    private currentNote: string | null = null;
    private clef: ClefType = 'treble';
    /**
     * The last draw requested before VexFlow finished loading. VexFlow comes
     * from a CDN <script>, so a hard reload can request a draw before
     * `window.Vex` exists. The request is stashed here and replayed by init().
     * Only the latest request is kept.
     */
    private pendingRender: (() => void) | null = null;
    private initAttempts = 0;

    constructor(containerId: string) {
        this.container = document.getElementById(containerId);

        if (!this.container) {
            console.error('Container not found:', containerId);
            return;
        }

        this.init();
    }

    /**
     * Apply theme-aware styling to SVG.
     * Uses currentColor bound to the --ink-primary CSS variable instead of a
     * baked-in computed color: on first paint the theme provider may not have
     * applied the .dark class yet, and a JS-resolved color would freeze the
     * wrong theme's ink into the SVG. currentColor re-resolves on theme flip.
     */
    private applyThemeToSVG(svg: SVGElement): void {
        svg.style.color = 'var(--ink-primary, #1a1714)';

        const elements = svg.querySelectorAll('path, line, rect, circle, ellipse');
        elements.forEach(el => {
            const element = el as SVGElement;
            if (!element.style.fill || element.style.fill === 'black' || element.style.fill === 'rgb(0, 0, 0)') {
                element.style.stroke = 'currentColor';
                element.style.fill = 'currentColor';
            }
        });
    }

    /**
     * Initialize VexFlow renderer
     */
    private init(): void {
        try {
            // Check if VexFlow is loaded (CDN script may not be ready yet)
            if (typeof Vex === 'undefined' || typeof window.Vex === 'undefined') {
                if (this.initAttempts++ < 20) {
                    // Try again after a short delay; a pending render replays once ready
                    setTimeout(() => this.init(), 500);
                } else {
                    console.error('VexFlow failed to load after multiple attempts');
                    this.showError('Unable to load music notation');
                }
                return;
            }

            if (!this.container) {
                console.error('Container not found, cannot initialize');
                return;
            }

            // Clear container
            this.container.innerHTML = '';

            // Create VexFlow renderer
            const VF = Vex.Flow;

            if (!VF || !VF.Renderer) {
                console.error('VexFlow.Renderer not available');
                return;
            }

            this.renderer = new VF.Renderer(
                this.container,
                VF.Renderer.Backends.SVG
            );

            // Size the canvas
            this.renderer.resize(500, 200);

            // Get drawing context
            this.context = this.renderer.getContext();
            this.initAttempts = 0;

            // Replay the draw requested before VexFlow finished loading
            if (this.pendingRender) {
                const replay = this.pendingRender;
                this.pendingRender = null;
                replay();
            }

        } catch (error) {
            console.error('Failed to initialize StaffRenderer:', error);
            this.showError('Unable to initialize music notation renderer');
        }
    }

    /**
     * Set the clef type
     * @param clef - 'treble' or 'bass'
     */
    setClef(clef: ClefType): void {
        this.clef = clef;
    }

    /**
     * Render a single note on the staff
     * @param vexflowNote - Note in VexFlow format (e.g., "c/4")
     * @param options - Rendering options
     */
    renderNote(vexflowNote: string, options: RenderOptions = {}): void {
        if (!this.context) {
            // VexFlow not ready yet — replay this draw once init() completes
            this.pendingRender = () => this.renderNote(vexflowNote, options);
            return;
        }

        try {
            const VF = Vex.Flow;

            if (!this.container) return;

            // Clear previous content
            this.context.clear();
            this.container.innerHTML = '';

            // Recreate context after clear
            this.renderer = new VF.Renderer(
                this.container,
                VF.Renderer.Backends.SVG
            );
            this.renderer.resize(500, 200);
            this.context = this.renderer.getContext();

            // Create a stave
            const stave = new VF.Stave(10, 20, 400);

            // Add clef
            stave.addClef(this.clef);

            // Draw the stave
            stave.setContext(this.context).draw();

            // Create note
            const note = new VF.StaveNote({
                keys: [vexflowNote],
                duration: 'w', // Whole note
                clef: this.clef
            });
            const noteAccidental = this.accidentalOf(vexflowNote);
            if (noteAccidental) note.addModifier(new VF.Accidental(noteAccidental), 0);

            // Create a voice and add the note
            const voice = new VF.Voice({
                num_beats: 4,
                beat_value: 4
            });
            voice.addTickable(note);

            // Format and draw
            new VF.Formatter()
                .joinVoices([voice])
                .format([voice], 350);

            voice.draw(this.context, stave);

            this.currentNote = vexflowNote;

            // Apply theme styling
            const svg = this.container?.querySelector('svg');
            if (svg) {
                this.applyThemeToSVG(svg);
                this.makeResponsive(svg);
            }

            // Apply highlight if specified
            if (options.highlight) {
                this.highlightNote();
            }

        } catch (error) {
            console.error('Failed to render note:', error);
            this.showError('Unable to display note');
        }
    }

    /**
     * Convert standard note name (e.g. "C4", "Eb5", "F#4") to VexFlow format ("c/4", "eb/5", "f#/4")
     */
    /** Scale the fixed-size VexFlow SVG down responsively inside narrow containers. */
    private makeResponsive(svg: SVGElement): void {
        const width = Number(svg.getAttribute('width')) || 700;
        const height = Number(svg.getAttribute('height')) || 200;
        if (!svg.getAttribute('viewBox')) svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
        svg.setAttribute('width', '100%');
        svg.removeAttribute('height');
        svg.style.maxWidth = `${width}px`;
        svg.style.height = 'auto';
        svg.style.display = 'block';
    }

    /** Accidental glyph for a VexFlow key ("bb/4" → "b", "b/4" → null, "f##/5" → "##"). */
    private accidentalOf(vexflowNote: string): string | null {
        const match = vexflowNote.match(/^[a-g](#{1,2}|b{1,2})\//);
        return match ? match[1] : null;
    }

    private toVexflowFormat(note: string): string {
        // Already in VexFlow format (has slash)
        if (note.includes('/')) return note;
        // Match standard format: note name + octave (single or double accidental)
        const match = note.match(/^([A-Ga-g](?:bb|##|[b#])?)(\d)$/);
        if (match) {
            return `${match[1].toLowerCase()}/${match[2]}`;
        }
        return note.toLowerCase();
    }

    /**
     * Render a chord (all notes stacked simultaneously) on the staff
     * @param notes - Array of notes in standard ("C4") or VexFlow ("c/4") format
     */
    renderChord(notes: string[]): void {
        if (!this.context) {
            this.pendingRender = () => this.renderChord(notes);
            return;
        }

        try {
            const VF = Vex.Flow;
            if (!this.container) return;

            const vexNotes = notes.map(n => this.toVexflowFormat(n));

            // Clear previous content
            this.context.clear();
            this.container.innerHTML = '';

            // Recreate context
            this.renderer = new VF.Renderer(this.container, VF.Renderer.Backends.SVG);
            this.renderer.resize(300, 180);
            this.context = this.renderer.getContext();

            // Create a stave
            const stave = new VF.Stave(10, 20, 250);
            stave.addClef(this.clef);
            stave.setContext(this.context).draw();

            // Create ONE stave note with ALL keys (chord)
            const chordNote = new VF.StaveNote({
                keys: vexNotes,
                duration: 'w',
                clef: this.clef
            });

            // Add accidentals
            vexNotes.forEach((vn, i) => {
                const accidental = this.accidentalOf(vn);
                if (accidental) chordNote.addModifier(new VF.Accidental(accidental), i);
            });

            const voice = new VF.Voice({ num_beats: 4, beat_value: 4 });
            voice.addTickable(chordNote);

            new VF.Formatter().joinVoices([voice]).format([voice], 200);
            voice.draw(this.context, stave);

            // Apply theme styling
            const svg = this.container?.querySelector('svg');
            if (svg) {
                this.applyThemeToSVG(svg);
                this.makeResponsive(svg);
            }

        } catch (error) {
            console.error('Failed to render chord on staff:', error);
            this.showError('Unable to display chord');
        }
    }

    /**
     * Render multiple notes on the staff (sequential)
     * @param notes - Array of notes in standard ("C4") or VexFlow ("c/4") format
     */
    renderNotes(notes: string[]): void {
        if (!this.context) {
            this.pendingRender = () => this.renderNotes(notes);
            return;
        }

        try {
            const VF = Vex.Flow;

            if (!this.container) return;

            // Clear previous content
            this.context.clear();
            this.container.innerHTML = '';

            // Recreate context
            this.renderer = new VF.Renderer(
                this.container,
                VF.Renderer.Backends.SVG
            );
            this.renderer.resize(700, 200);
            this.context = this.renderer.getContext();

            // Create a stave
            const stave = new VF.Stave(10, 20, 650);
            stave.addClef(this.clef);
            stave.setContext(this.context).draw();

            // Create notes (convert to VexFlow format)
            const staveNotes = notes.map(note => {
                const vn = this.toVexflowFormat(note);
                const sn = new VF.StaveNote({
                    keys: [vn],
                    duration: 'q',
                    clef: this.clef
                });
                const accidental = this.accidentalOf(vn);
                if (accidental) sn.addModifier(new VF.Accidental(accidental), 0);
                return sn;
            });

            // Create a voice
            const voice = new VF.Voice({
                num_beats: staveNotes.length,
                beat_value: 4
            });
            voice.addTickables(staveNotes);

            // Format and draw
            new VF.Formatter()
                .joinVoices([voice])
                .format([voice], 600);

            voice.draw(this.context, stave);

            // Apply theme styling
            const svg = this.container?.querySelector('svg');
            if (svg) {
                this.applyThemeToSVG(svg);
                this.makeResponsive(svg);
            }

        } catch (error) {
            console.error('Failed to render notes:', error);
            this.showError('Unable to display notes');
        }
    }

    /**
     * Render a scale on the staff
     * @param midiNotes - Array of MIDI note numbers
     */
    renderScale(midiNotes: number[]): void {
        const vexflowNotes = midiNotes
            .map(midi => MusicTheory.midiToVexflow(midi, this.clef))
            .filter((note): note is string => note !== null);

        if (vexflowNotes.length > 0) {
            this.renderNotes(vexflowNotes);
        }
    }

    /**
     * Highlight the current note (visual feedback)
     */
    private highlightNote(): void {
        if (!this.container) return;

        // Add a visual highlight to the SVG
        const svg = this.container.querySelector('svg');
        if (svg) {
            const noteheads = svg.querySelectorAll('.vf-notehead');
            noteheads.forEach(notehead => {
                (notehead as HTMLElement).style.fill = 'var(--accent-color, #3b82f6)';
            });
        }
    }

    /**
     * Show feedback for correct/incorrect note
     * @param isCorrect - Whether the note was correct
     */
    showFeedback(isCorrect: boolean): void {
        if (!this.container) return;

        if (!this.context) {
            this.pendingRender = () => this.showFeedback(isCorrect);
            return;
        }

        const svg = this.container.querySelector('svg');
        if (!svg) return;

        const noteheads = svg.querySelectorAll('.vf-notehead');
        const color = isCorrect ? 'var(--success-color, #10b981)' : 'var(--error-color, #ef4444)';

        noteheads.forEach(notehead => {
            const element = notehead as HTMLElement;
            element.style.fill = color;
            element.style.transition = 'fill 0.3s ease';
        });

        // Reset after animation
        setTimeout(() => {
            noteheads.forEach(notehead => {
                (notehead as HTMLElement).style.fill = '';
            });
        }, 500);
    }

    /**
     * Show error message
     * @param message - Error message to display
     */
    private showError(message: string): void {
        if (!this.container) return;

        this.container.innerHTML = `
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                height: 200px;
                color: var(--text-secondary);
                font-size: 1rem;
            ">
                ${message}
            </div>
        `;
    }

    /**
     * Show welcome message
     */
    showWelcome(): void {
        if (!this.container) return;

        if (!this.context) {
            this.pendingRender = () => this.showWelcome();
            return;
        }

        this.container.innerHTML = `
            <div style="
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 200px;
                color: var(--text-secondary);
                text-align: center;
                padding: 2rem;
            ">
                <p style="font-size: 3rem; margin-bottom: 0.5rem;">🎹</p>
                <p style="font-size: 1.2rem; font-weight: 600; margin-bottom: 0.5rem;">
                    Ready to Practice
                </p>
                <p style="font-size: 0.95rem;">
                    Click "Start Practice" to begin
                </p>
            </div>
        `;
    }

    /**
     * Clear the staff
     */
    clear(): void {
        if (this.context) {
            this.context.clear();
        }
        if (this.container) {
            this.container.innerHTML = '';
        }
        this.currentNote = null;
    }

    /**
     * Get the current note
     * @returns Current VexFlow note
     */
    getCurrentNote(): string | null {
        return this.currentNote;
    }
}

